import React, { useState, useCallback, useEffect, useMemo } from 'react'
import styled, { css } from 'styled-components'
import { Link } from 'gatsby'
import BrandIcon from '~src/assets/svg/brand.svg'
import { MenuIcon } from '~src/components/MenuIcon'
import { defaultFont, primaryFont } from '~src/common/cssTips'
import {
  defaultColor,
  sectionInlineSize,
  lgBreakPoint,
  mdBreakPoint,
  smBreakPoint,
  xsBreakPoint,
} from '~src/common/cssTips'
import { Button } from '~src/units/Button'
import { FROM_SHARE_ID, getShareId } from '~src/utils/fromShare'
import { sendKolLog } from '~src/utils/sendKolLog'
import { getLocalizedPath, getZhCnTranscript, isZhCN } from '~src/utils/locale'
import { gotoApp } from '~src/utils/gotoApp'

export const headerHeight = 80
export const headerMsgHeight = 54
const navItemPaddingForMdBreakPoint = 16
const navItemPaddingForLgBreakPoint = 20
const navItemPaddingForNoBreakPoint = 32

const navItemBlockStyle = css`
  display: inline-block;
  height: ${headerHeight}px;
  line-height: ${headerHeight}px;
  text-transform: uppercase;
  padding: 0 ${navItemPaddingForNoBreakPoint}px 0 0;
  box-sizing: border-box;
  transition: color 0.5s ease;
  font-weight: 600;

  @media screen and (max-width: ${lgBreakPoint}) {
    padding: 0 ${navItemPaddingForLgBreakPoint}px 0 0;

    &:first-child {
      padding-left: ${navItemPaddingForLgBreakPoint}px;
    }
  }

  @media screen and (max-width: ${mdBreakPoint}) {
    padding: 0 ${navItemPaddingForMdBreakPoint}px 0 0;

    &:first-child {
      padding-left: ${navItemPaddingForMdBreakPoint}px;
    }
  }

  @media screen and (max-width: ${smBreakPoint}) {
    padding: 0 ${navItemPaddingForMdBreakPoint}px;
    height: ${headerHeight * 0.75}px;
    line-height: ${headerHeight * 0.75}px;
  }
`

const Wrapper = styled.div`
  position: fixed;
  height: ${headerHeight}px;
  top: 0;
  left: 0;
  z-index: 1000;
  width: 100%;
  background-color: #fff;
  border-bottom: 1px solid transparent;

  @media screen and (max-width: ${smBreakPoint}) {
    height: ${headerHeight * 0.75}px;
  }

  &.scrolled {
    border-color: #e9e9e9;
  }
`

const Content = styled.div`
  ${sectionInlineSize};
  padding: 0 20px;
  height: 100%;
  display: flex;
  justify-content: space-between;
  text-transform: uppercase;
  align-items: center;

  @media screen and (max-width: ${lgBreakPoint}) {
    padding: 0 20px;
  }

  @media screen and (max-width: ${mdBreakPoint}) {
    padding: 0 10px;
  }
`

const NavBar = styled.nav`
  @media screen and (max-width: ${smBreakPoint}) {
    max-height: 0;
    overflow: hidden;
    transition: max-height 0.5s ease;

    &.opened {
      max-height: calc(100vh - ${headerHeight * 0.75}px);
      overflow: auto;
    }

    z-index: 2;
    position: absolute;
    left: 0;
    top: 100%;
    width: 100%;
    background-color: #fff;
  }
`

const BaseLinkCSS = css`
  display: inline-block;
  height: 100%;
  text-decoration: none;
  font-size: 14px;

  @media screen and (max-width: ${smBreakPoint}) {
    width: 100%;
    padding: 0;
    text-align: center;
    border-bottom: 1px solid #ebebeb;
  }
`

const BaseLink = styled.a`
  ${BaseLinkCSS}
`

const BrandLink = styled(Link)`
  text-decoration: none;

  svg {
    height: ${headerHeight / 2}px;

    @media screen and (max-width: ${mdBreakPoint}) {
      height: ${(headerHeight / 2) * 0.8}px;
    }
  }
`

const NavAppCSS = css`
  ${BaseLinkCSS};
  ${navItemBlockStyle};
  ${defaultFont};
  cursor: pointer;

  &:hover {
    ${primaryFont};
  }
`

const NavApp = styled(BaseLink)`
  &.active {
    ${primaryFont};
  }

  ${NavAppCSS};
`

const NavLink = styled(Link)<{ $dropdown?: boolean }>`
  ${NavAppCSS};

  &.active {
    ${primaryFont};
  }

  ${({ $dropdown }) => $dropdown && css`
    margin-right: 20px;

    &::after {
      position: absolute;
      top: 48%;
      margin-left: 10px;
      content: '';
      width: 0;
      height: 0;
      border-left: 4px solid transparent;
      border-right: 4px solid transparent;
      border-top: 4px solid currentColor;
      transition: transform 0.3s ease;
    }

    &:hover::after {
      transform: rotate(180deg);
    }

    @media screen and (max-width: ${smBreakPoint}) {
      margin-right: 0;
      position: relative;

      &::after {
        position: absolute;
        right: 20px;
        top: 50%;
        transform: translateY(-50%);
        margin-left: 0;
        transition: transform 0.3s ease;
      }

      &.opened::after {
        transform: translateY(-50%) rotate(180deg);
      }
    }
  `}
`

const FeaturesDropdown = styled.div`
  position: relative;
  display: inline-block;

  @media screen and (max-width: ${smBreakPoint}) {
    width: 100%;
    display: block;
  }
`

const FeaturesDropdownContent = styled.div`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1001;
  background-color: #fff;
  display: none;
  flex-direction: column;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;
  padding: 12px;
  gap: 12px;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  width: 240px;
  box-sizing: border-box;

  &::before {
    content: '';
    position: absolute;
    top: -8px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    border-bottom: 8px solid #fff;
  }

  ${FeaturesDropdown}:hover & {
    display: flex;
    opacity: 1;
    visibility: visible;
  }

  @media screen and (max-width: ${smBreakPoint}) {
    position: static;
    width: 100%;
    display: block;
    opacity: 1;
    visibility: visible;
    padding: 0;
    background-color: transparent;
    transition: none;
    max-height: 0;
    overflow: hidden;
    border-radius: 0;
    box-shadow: none;
    margin-top: 0;
    transform: none;

    &::before {
      display: none;
    }

    ${FeaturesDropdown}.opened & {
      max-height: 1000px;
    }
  }
`

const DropdownItem = styled(Link)`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 8px 16px;
  text-decoration: none;
  color: rgba(0, 0, 0, 0.9);
  font-size: 14px;
  font-weight: 500;
  font-family: 'PingFang SC', sans-serif;
  text-transform: none;
  transition: all 0.2s ease;
  background-color: transparent;
  border-radius: 0;
  border: none;
  cursor: pointer;
  white-space: normal;
  word-break: break-word;
  line-height: 1.4;
  box-sizing: border-box;

  &:hover {
    background-color: #F5F5F5;
    color: rgba(0, 0, 0, 0.9);
  }

  &.active {
    background-color: transparent;
    color: #04C747;
    font-weight: 600;
  }

  @media screen and (max-width: ${smBreakPoint}) {
    padding: 16px 20px;
    border-radius: 0;
    background-color: transparent;
    border-bottom: 1px solid #ebebeb;
    text-transform: none;
    justify-content: center;
    font-size: 12px;
    color: rgba(0, 0, 0, 0.54);
    position: relative;
    width: 100%;

    &::before {
      content: '>';
      position: absolute;
      left: 20px;
      color: rgba(0, 0, 0, 0.4);
      font-size: 16px;
    }

    &:hover {
      background-color: #e9e9e9;
      color: rgba(0, 0, 0, 0.9);
    }

    &.active {
      background-color: #04C747;
      color: #fff;

      &::before {
        color: rgba(255, 255, 255, 0.8);
      }
    }
  }
`

const NavSignIn = styled(NavApp)`
  display: inline-flex;
  align-items: center;
  justify-content: center;

  ${Button} {
    font-size: 14px;
    padding: 10px 34px;
  }

  &:last-child {
    padding-right: 0;
  }

  @media screen and (max-width: ${smBreakPoint}) {
    padding-right: 16px !important;

    ${Button} {
      background: unset;
      border: unset;
      color: ${defaultColor};
      font-weight: bold;
      padding: 0;
      text-transform: uppercase;
    }
  }
`

const NavMenuWrapper = styled.div`
  display: none;
  margin: 0 16px;

  @media screen and (max-width: ${smBreakPoint}) {
    display: block;
  }
`

const HeaderMsg = styled.div`
  ${sectionInlineSize};
  font-size: 16px;
  height: ${headerMsgHeight}px;
  background-color: #24cf5f;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  bottom: -${headerMsgHeight}px;
  padding: 0 15px;
  width: 100%;

  @media screen and (max-width: ${xsBreakPoint}) {
    font-size: 12px;
    bottom: -${headerMsgHeight * 0.75}px;
    height: ${headerMsgHeight * 0.75}px;
  }
`

export interface HeaderProps {
  path: string
}

export interface HeaderState {
  menuClosed: boolean
}

const copyText = (text: string) => {
  if (typeof document === 'undefined') {
    return
  }

  const textarea = document.createElement('textarea')
  textarea.value = text
  textarea.setAttribute('readonly', 'true')
  textarea.style.position = 'fixed'
  textarea.style.opacity = '0'
  document.body.appendChild(textarea)
  textarea.select()
  document.execCommand('copy')
  document.body.removeChild(textarea)
}

const copy = (shareId: string) => {
  if (typeof document === 'undefined') {
    return () => undefined
  }

  const text = `${FROM_SHARE_ID}=${shareId}`
  const links = Array.from(document.querySelectorAll<HTMLElement>('.app-download-link'))

  const handleClick = () => {
    copyText(text)
  }

  links.forEach((link) => {
    link.addEventListener('click', handleClick)
  })

  return () => {
    links.forEach((link) => {
      link.removeEventListener('click', handleClick)
    })
  }
}

export const Header = React.memo<HeaderProps>(function Header({ path }) {
  const [menuClosed, setMenuClosed] = useState(true)
  const [showMsg, setShowMsg] = useState(false)
  const [featuresDropdownOpen, setFeaturesDropdownOpen] = useState(false)

  const isZhLang = useMemo(() => isZhCN(path), [path])
  const transcript = useCallback(
    (localeIsZhCN: string, localeNonZhCN: string) =>
      getZhCnTranscript(localeIsZhCN, localeNonZhCN, path),
    [path],
  )

  useEffect(() => {
    const shareId = getShareId()
    if (!shareId) {
      return
    }

    const dispose = copy(shareId)

    setShowMsg(false)
    sendKolLog()

    return dispose
  }, [])

  useEffect(() => {
    setMenuClosed(true)
    setFeaturesDropdownOpen(false)
  }, [path])

  const handleMenuClick = useCallback(() => {
    setMenuClosed((closed) => !closed)
  }, [])

  const handleFeaturesClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    if (window.innerWidth <= parseInt(smBreakPoint, 10)) {
      e.preventDefault()
      setFeaturesDropdownOpen((open) => !open)
    }
  }, [])

  const handleTryClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault()
      gotoApp()
    },
    [],
  )

  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const detectScroll = () => {
      setScrolled(window.document.documentElement.scrollTop > 0)
    }

    window.document.addEventListener('scroll', detectScroll, false)

    return () => {
      window.document.removeEventListener('scroll', detectScroll, false)
    }
  }, [])

  const featuresSubPages = useMemo(() => {
    return [
      { name: transcript('功能概览', 'Features'), path: getLocalizedPath('/features/overview/', isZhLang) },
      { name: transcript('AI智能助手', 'AI Intelligent Assistant'), path: getLocalizedPath('/features/ai-assistant/', isZhLang) },
      { name: transcript('一体化平台', 'Integrated Platform'), path: getLocalizedPath('/features/cross-platforms/', isZhLang) },
      { name: transcript('树状结构', 'Tree Structure'), path: getLocalizedPath('/features/tree-structure/', isZhLang) },
      { name: transcript('多端同步', 'Cross-Platform Synchronization'), path: getLocalizedPath('/features/sync/', isZhLang) },
    ]
  }, [isZhLang, transcript])

  const brandLabel = transcript('前往 Elisi 首页', 'Go to the Elisi homepage')
  const homeIsActive = path === '/' || path.startsWith('/home') || path.startsWith('/home-zh')
  const pricingPath = getLocalizedPath('/pricing/', isZhLang)
  const pricingIsActive = path.startsWith('/pricing') || path.startsWith('/pricing-zh')
  const downloadPath = getLocalizedPath('/download/', isZhLang)
  const downloadIsActive = path.startsWith('/download') || path.startsWith('/download-zh')

  return (
    <Wrapper className={scrolled ? 'scrolled' : ''}>
      <Content>
        <BrandLink
          to={getLocalizedPath('/', isZhLang)}
          aria-label={brandLabel}
          title={brandLabel}
        >
          <BrandIcon aria-hidden="true" focusable="false" />
        </BrandLink>
        <NavBar className={menuClosed ? '' : 'opened'}>
          <NavLink to={getLocalizedPath('/', isZhLang)} className={homeIsActive ? 'active' : ''}>
            {transcript('主页', 'Home')}
          </NavLink>
          <FeaturesDropdown className={featuresDropdownOpen ? 'opened' : ''}>
            <NavLink
              className={`${path.startsWith('/features') ? 'active' : ''} ${featuresDropdownOpen ? 'opened' : ''}`}
              to={getLocalizedPath('/features/', isZhLang)}
              $dropdown
              onClick={handleFeaturesClick}
            >
              {transcript('功能', 'Features')}
            </NavLink>
            <FeaturesDropdownContent>
              {featuresSubPages.map((page) => (
                <DropdownItem
                  key={page.path}
                  to={page.path}
                  className={path.startsWith(page.path) ? 'active' : ''}
                >
                  {page.name}
                </DropdownItem>
              ))}
            </FeaturesDropdownContent>
          </FeaturesDropdown>
          <NavLink
            to={isZhLang ? '/help-zh/' : '/blog/'}
            className={path.startsWith(isZhLang ? '/help-zh' : '/blog') ? 'active' : ''}
          >
            {transcript('帮助中心', 'Blog')}
          </NavLink>
          <NavLink to={pricingPath} className={pricingIsActive ? 'active' : ''}>
            {transcript('价格', 'Pricing')}
          </NavLink>
          <NavLink to={getLocalizedPath('/about/', isZhLang)} className={path.startsWith(isZhLang ? '/about-zh' : '/about') ? 'active' : ''}>
            {transcript('关于', 'About')}
          </NavLink>
          <NavLink to={getLocalizedPath('/privacy/', isZhLang)} className={path.startsWith(isZhLang ? '/privacy-zh' : '/privacy') ? 'active' : ''}>
            {transcript('隐私政策', 'Privacy Policy')}
          </NavLink>
          <NavLink to={downloadPath} className={downloadIsActive ? 'active' : ''}>
            {transcript('下载', 'Download')}
          </NavLink>
          <NavSignIn onClick={handleTryClick} target="_blank">
            <Button>{transcript('开始使用', 'Start Now')}</Button>
          </NavSignIn>
        </NavBar>
        <NavMenuWrapper>
          <MenuIcon isOpen={!menuClosed} onClick={handleMenuClick} />
        </NavMenuWrapper>
      </Content>
      {showMsg ? (
        <HeaderMsg>
          Registered users who share elisi with others get a limited membership
          for free; click below to register for a trial~
        </HeaderMsg>
      ) : null}
    </Wrapper>
  )
})
